import type { TranslationTag } from '@ps/web-core-intl';
import { TournamentState } from '@ps/web-poker-tile-mappers';
import { Translation } from '@ps/web-shared-components';
import classNames from 'clsx';
import { secondsToMilliseconds } from 'date-fns';
import React from 'react';
import { useIntl } from 'react-intl';

import useCountdown from '../../../hooks/dates/useCountdown';
import type { Tournament } from '../../../types/MTT';

import {
  TXTCLI_Announced,
  TXTCLI_Cancelled,
  TXTCLI_Completed,
  TXTCLI_Flighted,
  TXTCLI_Full_tourn_state,
  TXTCLI_In_N0_min,
  TXTCLI_Late_RegX,
  TXTCLI_Late_Reg_XN0X,
  TXTCLI_Paused,
  TXTCLI_Phase,
  TXTCLI_Registering,
  TXTCLI_Restricted,
  TXTCLI_Running,
  TXTCLI_Seating
} from './TournamentTime.messages.json';
import styles from './TournamentTime.scss';

const STATE_DATA_MAP: {
  [K in Partial<TournamentState>]: {
    translation: TranslationTag;
    className: string;
  };
} = {
  [TournamentState.ANNOUNCED]: {
    translation: TXTCLI_Announced,
    className: styles.white
  },
  [TournamentState.CANCELLED]: {
    translation: TXTCLI_Cancelled,
    className: styles.white
  },
  [TournamentState.COMPLETED]: {
    translation: TXTCLI_Completed,
    className: styles.gray
  },
  [TournamentState.FLIGHTED]: {
    translation: TXTCLI_Flighted,
    className: styles.white
  },
  [TournamentState.FULL]: {
    translation: TXTCLI_Full_tourn_state,
    className: styles.white
  },
  [TournamentState.LATE_REGISTRATION]: {
    translation: TXTCLI_Late_RegX,
    className: styles.red
  },
  [TournamentState.PAUSED]: {
    translation: TXTCLI_Paused,
    className: styles.white
  },
  [TournamentState.PHASE]: {
    translation: TXTCLI_Phase,
    className: styles.white
  },
  [TournamentState.REGISTERING]: {
    translation: TXTCLI_Registering,
    className: styles.white
  },
  [TournamentState.RESTRICTED]: {
    translation: TXTCLI_Restricted,
    className: styles.white
  },
  [TournamentState.RUNNING]: {
    translation: TXTCLI_Running,
    className: styles.steam
  },
  [TournamentState.SEATING]: {
    translation: TXTCLI_Seating,
    className: styles.green
  }
};

const TournamentTime: React.FC<{ tournament: Tournament }> = ({ tournament }) => {
  const { formattedState: state, regEndInt, whenStartInt } = tournament;
  const targetDate = state === TournamentState.LATE_REGISTRATION ? regEndInt : whenStartInt;
  const { days, hours, minutes } = useCountdown(targetDate);
  const { formatDate } = useIntl();

  const currentState = STATE_DATA_MAP[state];
  if (!currentState) return null;

  let timeContent;

  switch (state) {
    case TournamentState.LATE_REGISTRATION:
      if (days > 0 || hours > 0) {
        timeContent = <Translation {...TXTCLI_Late_RegX} />;
      } else {
        timeContent = <Translation {...TXTCLI_Late_Reg_XN0X} values={{ 0: minutes }} />;
      }
      break;
    case TournamentState.ANNOUNCED:
    case TournamentState.REGISTERING:
    case TournamentState.SEATING:
      if (days === 0 && hours === 0) {
        timeContent = (
          <span className={styles.green}>
            <Translation {...TXTCLI_In_N0_min} values={{ 0: minutes }} />
          </span>
        );
      } else {
        timeContent = (
          <>
            {formatDate(secondsToMilliseconds(targetDate), {
              month: 'short',
              day: '2-digit'
            })}{' '}
            {formatDate(secondsToMilliseconds(targetDate), {
              hour: '2-digit',
              minute: '2-digit',
              hourCycle: 'h23'
            })}
          </>
        );
      }
      break;
    default:
      timeContent = <Translation {...currentState.translation} />;
      break;
  }

  return (
    <div
      className={classNames(styles.root, currentState.className)}
      data-testid="tournament-time"
      suppressHydrationWarning
    >
      {timeContent}
    </div>
  );
};

export default TournamentTime;
